import Icon from '../icon/Icon'
import { useQuickPassNotifications } from '../../_hooks/use-quickpass-notifications'
import {
  formatRelativeTime,
  getNotificationGlyph,
} from '../notifications-drawer/helpers'
import {
  TeaserCard,
  Glyph,
  Body,
  Eyebrow,
  HeadlineRow,
  Headline,
  Time,
  Body2,
  TrailingMeta,
  CountPill,
  Chevron,
} from './styled'
import { NotificationsTeaserProps } from './types'

/**
 * Compact dashboard banner that surfaces the most-recent unread
 * notification with a quick CTA into the drawer. Renders nothing when
 * there are no unread notifications (unless `showWhenEmpty` is true,
 * which is reserved for surfaces that always want a slot).
 *
 * @param {NotificationsTeaserProps} props
 * @returns {JSX.Element | null}
 */
const NotificationsTeaser = ({
  showWhenEmpty = false,
}: NotificationsTeaserProps): JSX.Element | null => {
  const { notifications, unreadCount, openDrawer } = useQuickPassNotifications()

  const latestUnread =
    notifications.find(entry => entry.isRead === false) ?? null

  if (latestUnread === null && showWhenEmpty === false) {
    return null
  }

  if (latestUnread === null) {
    return null
  }

  const additionalCount = unreadCount - 1

  return (
    <TeaserCard type="button" onClick={openDrawer}>
      <Glyph aria-hidden="true">
        <Icon icon={getNotificationGlyph(latestUnread.type)} />
      </Glyph>
      <Body>
        <Eyebrow>What&apos;s new</Eyebrow>
        <HeadlineRow>
          <Headline>{latestUnread.title}</Headline>
          <Time>{formatRelativeTime(latestUnread.createdAt)}</Time>
        </HeadlineRow>
        <Body2>{latestUnread.body}</Body2>
      </Body>
      <TrailingMeta>
        {additionalCount > 0 ? (
          <CountPill>+{additionalCount} more</CountPill>
        ) : null}
        <Chevron aria-hidden="true">›</Chevron>
      </TrailingMeta>
    </TeaserCard>
  )
}

export default NotificationsTeaser
